/**
 * L'HTML che arriva dal DB (articoli, FAQ, servizi convenzione, testi delle iniziative) contiene
 * link interni relativi alla radice, nel formato di sito-anutel (es. "/associazione/articolo/31637"),
 * oppure relativi alla PAGINA corrente con segmenti "./"/"../" ripetuti, ereditati dal vecchio sito
 * WebForms (es. "../../associazione/articolo/18713"): il browser li risolve a partire dall'URL
 * corrente, quindi lo stesso link funziona o si rompe a seconda di dove viene aperto. L'HTML può
 * anche contenere link al vecchio sito WebForms nel formato "ArticoloView.aspx?idart=N", da
 * convertire nella rotta equivalente "/associazione/articolo/N".
 * Questi `<a>` sono HTML semplice, non gestito dal router: ignorano il `basename` di dashboard-anutel
 * ("/utente") e puntano fuori dall'app. Questo hook DOMPurify globale aggiunge il prefisso `PUBLIC_URL`
 * ai soli link interni che puntano a rotte esistenti in dashboard-anutel, senza modificare i dati.
 * Se `PUBLIC_URL` è vuoto l'hook non si registra (no-op): nessun fallback, a differenza del
 * precedente rimedio provvisorio applicato alla sola pagina del servizio convenzione, ora rimosso.
 * Da rimuovere quando sito-anutel sostituirà dashboard-anutel in produzione.
 */
import DOMPurify from 'dompurify';

// Base dell'app dal build CRA (homepage). NESSUN fallback: vuota → hook non registrato (no-op).
export const APP_BASE = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');

// Rotte pubbliche/interne di dashboard-anutel raggiungibili da link nell'HTML da DB.
// Prefissi con "/" finale = match per inizio; senza "/" = match esatto o seguito da / ? #
export const APP_PATH_PREFIXES = ['/dashboard', '/documento/', '/associazione/articolo/', '/iniziativa/', '/convenzioni'];

// Rotte senza sotto-percorsi in App.js (nessun figlio, nessun wildcard "/*"): devono corrispondere
// SOLO in modo esatto, mai come prefisso. "/iniziative" (route FormazioneIniziative) è l'unico
// caso oggi: "/iniziative/data/…" non è una rotta dell'app, sono vecchi file immagine del sito
// WebForms che per puro caso iniziano con lo stesso nome.
const APP_EXACT_PATHS = ['/iniziative'];

const ANUTEL_HOST = /^(www\.)?anutel\.it$/i;

// Schema esplicito (http:, mailto:, tel:, javascript:, ...): un href con schema non è mai relativo.
const SCHEME_RE = /^[a-z][a-z0-9+.-]*:/i;

// Vecchio sito WebForms: "/associazione/ArticoloView.aspx?idart=N" → "/associazione/articolo/N".
const ARTICOLO_VIEW_PATH = '/associazione/articoloview.aspx';

// Rispetta il confine del percorso: "/dashboardx" non corrisponde a "/dashboard".
// `path` è già privo di query string e ancora (solo il pathname).
function matchesAppPrefix(path) {
  if (APP_EXACT_PATHS.includes(path)) return true;
  return APP_PATH_PREFIXES.some((prefix) => {
    if (prefix.endsWith('/')) return path.startsWith(prefix);
    return path === prefix || path.startsWith(`${prefix}/`);
  });
}

// Scompone un href (o un path+query+hash già normalizzato) nelle sue 3 parti.
function splitHref(href) {
  const m = /^([^?#]*)(\?[^#]*)?(#.*)?$/.exec(href);
  return { path: m[1], search: m[2] || '', hash: m[3] || '' };
}

// Converte "/associazione/ArticoloView.aspx?idart=N" (case-insensitive sul path, "idart" sensibile
// alle maiuscole nella query, come il resto della querystring) in "/associazione/articolo/N".
// Restituisce null se il path non corrisponde o idart è assente/non numerico.
function convertArticoloView(path, search) {
  if (path.toLowerCase() !== ARTICOLO_VIEW_PATH) return null;
  const idart = new URLSearchParams(search).get('idart');
  if (!idart || !/^\d+$/.test(idart)) return null;
  return `/associazione/articolo/${idart}`;
}

export function rewriteAppHref(href, appBase = APP_BASE) {
  if (!appBase || typeof href !== 'string' || !href) return href;

  const trimmed = href.trim();

  // Relativo a root: un solo "/" iniziale (esclude "//…", schemi, "#…", relativi senza "/").
  if (/^\/(?!\/)/.test(trimmed)) {
    const { path, search, hash } = splitHref(trimmed);

    if (path === appBase || path.startsWith(`${appBase}/`)) return trimmed; // niente doppio prefisso

    const articolo = convertArticoloView(path, search);
    if (articolo) return appBase + articolo + hash;

    if (matchesAppPrefix(path)) return appBase + trimmed;
    return trimmed;
  }

  // Assoluto: solo http/https, solo host anutel.it/www.anutel.it (mai i sottodomini).
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      if (!ANUTEL_HOST.test(url.hostname)) return trimmed;
      if (url.pathname === appBase || url.pathname.startsWith(`${appBase}/`)) return trimmed;

      const articolo = convertArticoloView(url.pathname, url.search);
      if (articolo) {
        url.pathname = appBase + articolo;
        url.search = '';
        return url.toString();
      }

      if (!matchesAppPrefix(url.pathname)) return trimmed;

      url.pathname = appBase + url.pathname;
      return url.toString();
    } catch {
      return trimmed;
    }
  }

  // Relativo alla PAGINA corrente (WebForms): non inizia con "/", "#", "?" né con uno schema.
  if (!trimmed.startsWith('/') && !trimmed.startsWith('#') && !trimmed.startsWith('?') && !SCHEME_RE.test(trimmed)) {
    const stripped = trimmed.replace(/^(?:\.{1,2}\/)+/, '');
    const candidate = `/${stripped}`;
    const { path, search, hash } = splitHref(candidate);

    // Dopo aver tolto "./"/"../" il link punta già, esplicitamente, dentro l'app (basename incluso):
    // non dipende più dalla pagina di partenza, va restituito così com'è.
    if (path === appBase || path.startsWith(`${appBase}/`)) return candidate;

    const articolo = convertArticoloView(path, search);
    if (articolo) return appBase + articolo + hash;

    if (matchesAppPrefix(path)) return appBase + candidate;
    return trimmed; // nessuna corrispondenza: href originale invariato, non il candidato
  }

  return trimmed;
}

if (APP_BASE && !DOMPurify.__anutelAppLinkHookRegistered) {
  DOMPurify.__anutelAppLinkHookRegistered = true;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (node.tagName !== 'A' || !node.hasAttribute('href')) return;
    const href = node.getAttribute('href');
    const nuovo = rewriteAppHref(href);
    if (nuovo !== href) node.setAttribute('href', nuovo);
  });
}
